import {
  tableDataToBlock, blockToTableData, type TableBlockContent,
} from "@/components/editor/table-grid/tableModel";
import type { TableSummary } from "./useTables";

/**
 * Putting a table into a paper — the two ways, and what each one means.
 *
 * The choice happens **at insert, not at divergence**: asking here reaches
 * someone thinking about this table and this paper, while asking when they
 * diverge interrupts someone fixing a typo.
 *
 * **Link is the default**, because reuse is why the entity exists and because
 * copying is the recoverable mistake of the two — a copy you wanted linked is a
 * re-insert, while a link you wanted copied may already have touched two other
 * papers.
 *
 * Design: SPEC.md §4a.
 */

export type InsertMode = "link" | "copy";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface InsertResult {
  /** The table now in the document — the original when linked, a new one when copied. */
  tableId: string;
  /** The block rendering it. */
  blockId: string | null;
  mode: InsertMode;
}

/** The block content a table puts into a paper: its grid, caption and label. */
export function tableBlockContent(table: TableSummary): TableBlockContent {
  const data = blockToTableData(table.content as never);
  return tableDataToBlock({ ...data, caption: table.caption, label: table.label });
}

/**
 * How the table's block gets into the paper; resolves to the new block's id, or
 * null when it did not go in.
 *
 * Two ways, because there are two situations. From the Tables list or the tool,
 * the paper is not open, so the server adds the block (`placeViaServer`). From
 * the editor, the paper IS open, and the canvas owns it: a block the server adds
 * behind its back is not shown, and the canvas's next save can drop it. There
 * the editor puts the block in itself (see `EditorTablePicker`).
 */
export type PlaceBlock = (documentId: string, content: TableBlockContent) => Promise<string | null>;

export function placeViaServer(
  fetchWithAuth: Fetch,
  /** Where in the paper: the new block takes this sortOrder and later ones
   *  shift down. Absent, it goes at the end. */
  sortOrder?: number | null,
): PlaceBlock {
  return async (documentId, content) => {
    const res = await fetchWithAuth(`/api/documents/${documentId}/blocks`, {
      method: "POST",
      body: JSON.stringify(sortOrder == null ? { type: "table", content } : { type: "table", content, sortOrder }),
    });
    if (!res.ok) return null;
    const block = (await res.json().catch(() => null)) as { id?: string } | null;
    return block?.id ?? null;
  };
}

/** Record that `documentId` uses `tableId`, through the block that renders it. */
async function attach(fetchWithAuth: Fetch, tableId: string, documentId: string, blockId: string) {
  const res = await fetchWithAuth(`/api/tables/${tableId}/documents/${documentId}?blockId=${blockId}`, {
    method: "POST",
  });
  return res.ok;
}

/**
 * Link: one row, referenced from another paper. Editing it later reaches every
 * paper that references it — which is the feature, not a hazard to warn about.
 */
export async function linkTableIntoDocument(
  fetchWithAuth: Fetch,
  table: TableSummary,
  documentId: string,
  place: PlaceBlock = placeViaServer(fetchWithAuth),
): Promise<InsertResult | null> {
  const blockId = await place(documentId, tableBlockContent(table));
  if (!blockId) return null;
  if (!(await attach(fetchWithAuth, table.id, documentId, blockId))) return null;
  return { tableId: table.id, blockId, mode: "link" };
}

/**
 * Copy: a new table of its own, for this paper only from now on.
 *
 * It records `copiedFrom`, so the fork stays legible — a reference model does
 * not die of forking, it dies of forking invisibly. A copy is a first-class
 * table, listed and named, never an orphan.
 */
export async function copyTableIntoDocument(
  fetchWithAuth: Fetch,
  table: TableSummary,
  documentId: string,
  place: PlaceBlock = placeViaServer(fetchWithAuth),
): Promise<InsertResult | null> {
  const made = await fetchWithAuth("/api/tables", {
    method: "POST",
    body: JSON.stringify({
      caption: table.caption,
      label: table.label,
      content: table.content,
      copiedFrom: table.id,
    }),
  });
  if (!made.ok) return null;
  const copy = (await made.json()) as { id: string };

  const blockId = await place(documentId, tableBlockContent(table));
  if (!blockId) return null;
  if (!(await attach(fetchWithAuth, copy.id, documentId, blockId))) return null;
  return { tableId: copy.id, blockId, mode: "copy" };
}

/** What the Link button says, given how many papers already use the table. */
export function linkConsequence(documentCount: number): string {
  const after = documentCount + 1;
  return after <= 1 ? "edits reach this paper" : `edits reach all ${after} papers`;
}
